import { Box } from "@mui/material";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import FunctionalAreas from "components/common/FunctionalAreas";
import Spinner from "components/common/Spinner/Spinner";
import BasicTable from "components/common/Table";
import Timing from "components/common/Timing";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { uniq, uniqBy } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE, progressTypes } from "utils/CommonConstants";
import { getHoursAndMin, userNameToHltId } from "utils/CommonFunctions";
import { consultantEngagementTypes } from "utils/Types";
import FilterButtons from "../AboveProperty/FilterButtons";
import { getAssignmentData, getPlanProgressData, getUserPlanProgressData, getpropertyProgressData } from "./Data";
import { fetchAssigneeDetailsProgressAsync, fetchProgressTableInfoAsync, setProgressLoader } from "./Progress.slice";
import "./index.scss";

interface Props {
  progressType: string;
}
let isMountedFilters = false;
let isMountedAssociates = false;

const ProgressInfo = (props: Props) => {
  const { progressType } = props;
  const [pageNo, setPageNo] = useState(0);
  const summaryDetail = useAppSelector((state) => state.progress.progressInfo.summaryDetail);
  const { tableDetails: tableData, totalRecords } =
    useAppSelector((state) => state.progress?.progressInfo?.[progressType]) || [];
  const userHtlids = useAppSelector((state) => state.progress.progressInfo.userHtlids);
  const userInncodes = useAppSelector((state) => state.progress.progressInfo.userInncodes);
  const progressLoader = useAppSelector((state) => state.progress.progressLoader);
  const allUsers = useAppSelector((state) => state.progress.progressInfo.allUsers);
  const filters = useAppSelector((state: any) => state.aboveProperty.filters.corpUserProgress);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);

  const ownerOptions = uniqBy(allUsers?.users, "hltId");

  const { isMobileScreen } = useContext(ThemeContext);
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const params = useParams();

  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const cmsId = searchParams.get("cmsId");
  const users = searchParams.get("users");
  const inncode =
    progressType === progressTypes.plan ? summaryDetail?.implementedInncodes?.join(",") : searchParams.get("inncode");

  const progressTypeDetails: any = {
    [progressTypes.plan]: {
      label: "plan",
      buttonLabel: "View Plan Details",
      nameKey: "planName",
      tableDetails: getPlanProgressData(tableData, allUsers, navigate),
      url: `/plandetails/${cmsId}`
    },
    [progressTypes.property]: {
      label: "property",
      buttonLabel: "View Property Details",
      description: { label: "All property plans", detail: "Use the button to see all the plans of the property" },
      nameKey: "propertyName",
      tableDetails: getpropertyProgressData(tableData, allUsers, navigate, functionalAreaList),
      url: `/property/${inncode}`
    },
    [progressTypes.user]: {
      label: "user",
      buttonLabel: "View User Details",
      description: {
        label: "All user plans",
        detail: "You’re consulting the assignments you made to this user. Use the button to see all of his plans"
      },
      nameKey: "innCode",
      tableDetails: getUserPlanProgressData(tableData, allUsers, navigate, functionalAreaList),
      url: `/userview/${users}?inncode=${inncode}`
    }
  };

  const toQueryParams = () => {
    return Object.entries(filters[progressType])
      .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(",") : value}`)
      .join("&");
  };

  const getData = async (pageNumber?: number) => {
    let payload: any = { id: params?.listId, type: progressType, page: pageNumber ?? pageNo };
    if (progressType === progressTypes.plan) {
      const { inncode: filterInncode, engagements, status, searchText } = filters[progressType];
      const url = `inncode=${
        filterInncode?.length ? filterInncode.join(",") : inncode
      }&status=${status}&engagement=${engagements}&searchText=${searchText}`;
      payload = {
        ...payload,
        payloadUrl: `cmsId=${cmsId}&${url}`
      };
    } else if (progressType === progressTypes.property) {
      const { searchText, functionalAreas, owners: users } = filters[progressType];
      const htlIds = users?.length > 0 ? userNameToHltId(users, ownerOptions)?.join(",") : "";

      const url = `functionalAreas=${functionalAreas?.join(",")}&users=${htlIds}&searchText=${searchText}`;
      payload = {
        ...payload,
        payloadUrl: `inncode=${inncode}&includeImplementedInnCodeUserMappingsOnly=${true}&${url}`
      };
    } else if (progressType === progressTypes.user) {
      const url = toQueryParams();
      payload = { ...payload, payloadUrl: `inncode=${inncode}&users=${users}&${url}` };
    }

    const tableList = await dispatch(fetchProgressTableInfoAsync(payload));
    const hltIds = uniq(tableList?.payload?.res?.mappings?.map((item: any) => item.hltId));
    if (!isMountedAssociates && hltIds) {
      const data = {
        value: hltIds && hltIds?.join(","),
        size: hltIds && hltIds?.length
      };

      await dispatch(fetchAssigneeDetailsProgressAsync(data));
    }
    isMountedAssociates = true;
    dispatch(setProgressLoader(false));
  };

  useEffect(() => {
    getData();
    return () => {
      isMountedFilters = false;
      isMountedAssociates = false;
    };
  }, [progressType]);

  useEffect(() => {
    if (pageNo > 0) getData();
  }, [pageNo]);

  useEffect(() => {
    if (isMountedFilters) {
      getData(0);
      setPageNo(0);
    }
    isMountedFilters = true;
  }, [filters[progressType]]);
  return (
    <div className="progress-info-component">
      {progressLoader && <Spinner backdropVisible />}
      <div className="progress-info-summary-container">
        <div
          className={`progress-info-summary-left-wrapper ${
            progressType !== progressTypes.plan ? "property-user-summary" : ""
          }`}
        >
          {progressType !== progressTypes.plan && (
            <Typography variant={"body1"} color={tokens.Text.General.PrimaryHeadline}>
              {progressType === progressTypes.user ? summaryDetail?.displayName : summaryDetail?.inncode}
            </Typography>
          )}

          <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline} className="left-wrapper-header">
            {summaryDetail[`${progressTypeDetails[progressType]?.nameKey}`]}
          </Typography>
          {progressType !== "plan" && <> {getAssignmentData(progressType, summaryDetail)}</>}

          {progressType === progressTypes.plan && summaryDetail?.functionalAreas && (
            <div className="left-wrapper-data-info-plan">
              <Box>
                <FunctionalAreas functionalAreaIds={summaryDetail?.functionalAreas} />
              </Box>
              <Timing duration={getHoursAndMin(summaryDetail?.timeEstimateMinutesFromCmsPlan)} label="Effort" />
            </div>
          )}
        </div>
        {progressType === progressTypes.plan && (
          <div className="right-Wrapper-plan">
            {" "}
            <Button
              label={progressTypeDetails[progressType]?.buttonLabel}
              size="Small"
              onClick={() =>
                navigate(progressTypeDetails[progressType]?.url, {
                  state: {
                    innCode: DEFAULT_CORP_INNCODE
                  }
                })
              }
            />
          </div>
        )}

        {progressType !== progressTypes.plan && (
          <div className="right-Wrapper-property-user">
            {" "}
            <div className="right-Wrapper-property-user-text">
              <Typography variant={"body2"} showBold color={tokens.Text.General.PrimaryHeadline}>
                {progressTypeDetails?.[progressType]?.description?.label}
              </Typography>
              <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
                {progressTypeDetails[progressType]?.description?.detail}
              </Typography>
            </div>
            <Button
              label={progressTypeDetails[progressType]?.buttonLabel}
              onClick={() => navigate(progressTypeDetails[progressType]?.url)}
              size="Small"
            />
          </div>
        )}
      </div>
      <div id="progressDiv" className="progress-info-table-container">
        <FilterButtons
          parentView="corpUserProgress"
          filterType={progressType}
          isSearchBarVisible={progressType !== progressTypes.plan}
          isOwnersVisible={progressType === progressTypes.property}
          isFunctionalAreaVisible={progressType !== progressTypes.plan}
          isEngagementVisible={progressType === progressTypes.plan}
          isStatusVisible={progressType === progressTypes.plan}
          isInncodeVisible={progressType === progressTypes.plan}
          optionsList={{
            inncode: summaryDetail?.implementedInncodes,
            owner: ownerOptions,
            engagements: consultantEngagementTypes ?? {}
          }}
        />
        <BasicTable
          scrollableTarget="progressDiv"
          totalRecords={totalRecords}
          pagination={{ pageNo: pageNo, setPageNo: setPageNo }}
          noDataMessage={translate("common.noResults")}
          data={progressTypeDetails[progressType]?.tableDetails}
        />
      </div>
    </div>
  );
};
export default ProgressInfo;
